import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Leaderboard = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch leaderboard data
  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Fetching leaderboard from:', `${process.env.REACT_APP_API_URL}/api/quiz/leaderboard`);
        
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/quiz/leaderboard`);
        console.log('Leaderboard Response Status:', response.status);
        console.log('Leaderboard Response Data:', response.data);
        console.log('Data type:', typeof response.data);
        console.log('Is Array:', Array.isArray(response.data));
        console.log('Data length:', response.data?.length);
        
        if (response.data && Array.isArray(response.data)) {
          setLeaderboard(response.data);
          if (response.data.length === 0) {
            setError('No quiz results found. Complete some quizzes to see leaderboard data.');
          }
        } else {
          console.warn('Unexpected data format:', response.data);
          setLeaderboard([]);
          setError('Received unexpected data format from server.');
        }
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
        console.error('Error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message
        });
        
        if (error.response?.status === 404) {
          setError('Leaderboard endpoint not found. Please check your backend API.');
        } else if (error.response?.status === 500) {
          setError('Server error occurred. Check your database connection and try again.');
        } else if (error.code === 'ECONNREFUSED') {
          setError('Cannot connect to server. Make sure your backend is running.');
        } else {
          setError(`Failed to load leaderboard: ${error.message}`);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, []);

  const retryFetch = () => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        setError(null);
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const response = await axios.get(`${apiUrl}/api/quiz/leaderboard`);
        setLeaderboard(response.data || []);
      } catch (error) {
        setError(error.response?.status === 404 
          ? 'Leaderboard endpoint not found. Please check your backend API.'
          : 'Failed to load leaderboard data. Please try again later.'
        );
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  };

  // Mock data for demonstration when API fails
  const mockLeaderboard = [
    { username: 'Alex Johnson', totalScore: 2850, fastestTime: 45, avatar: 'AJ' },
    { username: 'Sarah Chen', totalScore: 2720, fastestTime: 52, avatar: 'SC' },
    { username: 'Mike Rodriguez', totalScore: 2680, fastestTime: 48, avatar: 'MR' },
    { username: 'Emma Davis', totalScore: 2540, fastestTime: 58, avatar: 'ED' },
    { username: 'James Wilson', totalScore: 2420, fastestTime: 63, avatar: 'JW' },
  ];

  const displayData = leaderboard.length > 0 ? leaderboard : (error ? mockLeaderboard : []);

  return (
    <div className="leaderboard-container">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        .leaderboard-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          position: relative;
          overflow: hidden;
          padding: 2rem 1rem;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .leaderboard-container::before {
          content: '';
          position: absolute;
          top: 10%;
          right: 15%;
          width: 150px;
          height: 150px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.08);
          animation: float 6s ease-in-out infinite;
        }

        .leaderboard-container::after {
          content: '';
          position: absolute;
          bottom: 15%;
          left: 10%;
          width: 100px;
          height: 100px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.05);
          animation: float 4s ease-in-out infinite;
          animation-delay: 2s;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(10deg); }
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes bounceIn {
          0% {
            opacity: 0;
            transform: scale(0.3);
          }
          50% {
            opacity: 1;
            transform: scale(1.05);
          }
          70% {
            transform: scale(0.9);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        .pulse-element {
          position: absolute;
          top: 30%;
          left: 75%;
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.06);
          animation: pulse 4s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.1); }
        }

        .leaderboard-wrapper {
          max-width: 800px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .header-section {
          text-align: center;
          margin-bottom: 3rem;
        }

        .header-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(255, 255, 255, 0.15);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 500;
          margin-bottom: 1.5rem;
          backdrop-filter: blur(10px);
        }

        .main-title {
          font-size: clamp(2.5rem, 5vw, 3.5rem);
          font-weight: 900;
          color: white;
          margin-bottom: 0.5rem;
          text-shadow: 0 4px 20px rgba(0,0,0,0.3);
          animation: slideIn 0.8s ease-out;
        }

        .gradient-text {
          background: linear-gradient(45deg, #FFD700, #FF6B6B, #4ECDC4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .subtitle {
          color: rgba(255, 255, 255, 0.9);
          font-weight: 300;
          font-size: 1.1rem;
          animation: slideIn 0.8s ease-out 0.2s both;
        }

        .leaderboard-content {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: 3rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          animation: slideIn 0.6s ease-out 0.4s both;
          position: relative;
          overflow: hidden;
        }

        .leaderboard-content::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #FFD700, #FF6B6B, #4ECDC4, #667eea);
          border-radius: 24px 24px 0 0;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          color: #6b7280;
        }

        .loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid rgba(102, 126, 234, 0.2);
          border-top: 4px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1.5rem;
        }

        .loading-text {
          font-size: 1.1rem;
          font-weight: 500;
        }

        .error-container {
          text-align: center;
          padding: 3rem 2rem;
        }

        .error-icon {
          width: 80px;
          height: 80px;
          background: rgba(239, 68, 68, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          color: #dc2626;
        }

        .error-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 1rem;
        }

        .error-message {
          color: #6b7280;
          font-size: 1rem;
          margin-bottom: 2rem;
          line-height: 1.6;
        }

        .retry-btn {
          background: linear-gradient(45deg, #667eea 30%, #764ba2 90%);
          color: white;
          border: none;
          padding: 1rem 2rem;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
        }

        .retry-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          color: #6b7280;
        }

        .empty-icon {
          width: 100px;
          height: 100px;
          background: rgba(156, 163, 175, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          color: #9ca3af;
        }

        .empty-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 1rem;
        }

        .empty-message {
          font-size: 1rem;
          line-height: 1.6;
        }

        .podium-container {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          margin-bottom: 3rem;
          align-items: end;
        }

        .podium-item {
          text-align: center;
          animation: bounceIn 0.8s ease-out;
        }

        .podium-item:nth-child(1) { animation-delay: 0.2s; }
        .podium-item:nth-child(2) { animation-delay: 0s; }
        .podium-item:nth-child(3) { animation-delay: 0.4s; }

        .podium-avatar {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          margin: 0 auto 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.5rem;
          color: white;
          position: relative;
        }

        .gold {
          background: linear-gradient(45deg, #FFD700, #FFA500);
          box-shadow: 0 8px 32px rgba(255, 215, 0, 0.4);
        }

        .silver {
          background: linear-gradient(45deg, #C0C0C0, #A8A8A8);
          box-shadow: 0 8px 32px rgba(192, 192, 192, 0.4);
        }

        .bronze {
          background: linear-gradient(45deg, #CD7F32, #B8860B);
          box-shadow: 0 8px 32px rgba(205, 127, 50, 0.4);
        }

        .crown {
          position: absolute;
          top: -10px;
          left: 50%;
          transform: translateX(-50%);
          font-size: 1.5rem;
        }

        .podium-name {
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 0.5rem;
          font-size: 1.1rem;
        }

        .podium-score {
          color: #6b7280;
          font-weight: 500;
          font-size: 0.9rem;
        }

        .podium-base {
          background: linear-gradient(145deg, #f3f4f6, #e5e7eb);
          border-radius: 8px;
          padding: 1rem;
          margin-top: 1rem;
          box-shadow: inset 0 2px 4px rgba(0,0,0,0.1);
        }

        .first-place .podium-base { height: 60px; }
        .second-place .podium-base { height: 45px; }
        .third-place .podium-base { height: 30px; }

        .remaining-list {
          margin-top: 2rem;
        }

        .list-header {
          font-size: 1.2rem;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .leaderboard-item {
          background: rgba(255, 255, 255, 0.8);
          border: 2px solid rgba(209, 213, 219, 0.2);
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 1rem;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 1.5rem;
          backdrop-filter: blur(10px);
          animation: slideIn 0.5s ease-out;
        }

        .leaderboard-item:hover {
          border-color: rgba(102, 126, 234, 0.3);
          box-shadow: 0 8px 30px rgba(0,0,0,0.08);
          transform: translateY(-2px);
        }

        .rank-number {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(45deg, #667eea 30%, #764ba2 90%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.1rem;
        }

        .user-avatar {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: linear-gradient(45deg, #f093fb 30%, #f5576c 90%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.2rem;
        }

        .user-info {
          flex: 1;
        }

        .user-name {
          font-weight: 700;
          color: #1f2937;
          font-size: 1.1rem;
          margin-bottom: 0.25rem;
        }

        .user-stats {
          color: #6b7280;
          font-size: 0.9rem;
        }

        .score-badge {
          background: linear-gradient(45deg, #10b981 30%, #059669 90%);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-weight: 700;
          font-size: 1rem;
          box-shadow: 0 4px 16px rgba(16, 185, 129, 0.3);
        }

        .demo-notice {
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 12px;
          padding: 1rem;
          margin-bottom: 2rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #1d4ed8;
        }

        .demo-icon {
          width: 24px;
          height: 24px;
          background: rgba(59, 130, 246, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .leaderboard-container {
            padding: 1rem 0.5rem;
          }
          
          .leaderboard-content {
            padding: 2rem 1.5rem;
          }

          .podium-container {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }

          .podium-item {
            display: flex;
            align-items: center;
            text-align: left;
            gap: 1rem;
          }

          .podium-avatar {
            width: 60px;
            height: 60px;
            margin: 0;
            font-size: 1.2rem;
          }

          .podium-base {
            display: none;
          }

          .leaderboard-item {
            flex-direction: column;
            text-align: center;
            gap: 1rem;
          }

          .user-info {
            text-align: center;
          }
        }
      `}</style>

      <div className="pulse-element"></div>
      
      <div className="leaderboard-wrapper">
        {/* Header Section */}
        <div className="header-section">
          <div className="header-chip">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M5,16L3,14V10L5,8H7L9,10V11H15V10L17,8H19L21,10V14L19,16H17L15,14V13H9V14L7,16H5Z"/>
            </svg>
            Quiz Champions
          </div>
          
          <h1 className="main-title">
            <span className="gradient-text">Leaderboard</span>
          </h1>
          
          <p className="subtitle">
            See who's dominating the quiz arena
          </p>
        </div>

        {/* Content */}
        <div className="leaderboard-content">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <div className="loading-text">Loading leaderboard...</div>
            </div>
          ) : error ? (
            <div className="error-container">
              <div className="error-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11,15H13V17H11V15M11,7H13V13H11V7M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20Z"/>
                </svg>
              </div>
              <div className="error-title">Unable to Load Leaderboard</div>
              <div className="error-message">
                {error}
                <br />
                <strong>Showing demo data below for reference.</strong>
              </div>
              <button className="retry-btn" onClick={retryFetch}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"/>
                </svg>
                Try Again
              </button>
            </div>
          ) : displayData.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M5,16L3,14V10L5,8H7L9,10V11H15V10L17,8H19L21,10V14L19,16H17L15,14V13H9V14L7,16H5Z"/>
                </svg>
              </div>
              <div className="empty-title">No Rankings Yet</div>
              <div className="empty-message">
                Complete some quizzes to see the leaderboard populate with rankings and scores.
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="demo-notice">
                  <div className="demo-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,17H13V11H11V17Z"/>
                    </svg>
                  </div>
                  <span><strong>Demo Mode:</strong> Displaying sample data since the API endpoint is not available.</span>
                </div>
              )}

              {/* Top 3 Podium */}
              {displayData.length >= 3 && (
                <div className="podium-container">
                  {/* Second Place */}
                  <div className="podium-item second-place">
                    <div className="podium-avatar silver">
                      {displayData[1].avatar || displayData[1].username.charAt(0)}
                    </div>
                    <div className="podium-name">{displayData[1].username}</div>
                    <div className="podium-score">{displayData[1].totalScore} pts</div>
                    <div className="podium-base"></div>
                  </div>

                  {/* First Place */}
                  <div className="podium-item first-place">
                    <div className="podium-avatar gold">
                      <span className="crown">👑</span>
                      {displayData[0].avatar || displayData[0].username.charAt(0)}
                    </div>
                    <div className="podium-name">{displayData[0].username}</div>
                    <div className="podium-score">{displayData[0].totalScore} pts</div>
                    <div className="podium-base"></div>
                  </div>

                  {/* Third Place */}
                  <div className="podium-item third-place">
                    <div className="podium-avatar bronze">
                      {displayData[2].avatar || displayData[2].username.charAt(0)}
                    </div>
                    <div className="podium-name">{displayData[2].username}</div>
                    <div className="podium-score">{displayData[2].totalScore} pts</div>
                    <div className="podium-base"></div>
                  </div>
                </div>
              )}

              {/* Remaining Rankings */}
              {displayData.length > 3 && (
                <div className="remaining-list">
                  <div className="list-header">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9,5V9H21V7.5L16,3L21,7.5V5H9M21,11H9V21H21V11M17,12V18H11V12H17Z"/>
                    </svg>
                    Rankings
                  </div>
                  
                  {displayData.slice(3).map((entry, index) => (
                    <div key={index + 3} className="leaderboard-item" style={{ animationDelay: `${(index + 3) * 0.1}s` }}>
                      <div className="rank-number">{index + 4}</div>
                      <div className="user-avatar">
                        {entry.avatar || entry.username.charAt(0)}
                      </div>
                      <div className="user-info">
                        <div className="user-name">{entry.username}</div>
                        <div className="user-stats">
                          Fastest Time: {entry.fastestTime}s
                        </div>
                      </div>
                      <div className="score-badge">
                        {entry.totalScore}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Show all items if less than 3 */}
              {displayData.length > 0 && displayData.length < 3 && (
                <div className="remaining-list">
                  <div className="list-header">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9,5V9H21V7.5L16,3L21,7.5V5H9M21,11H9V21H21V11M17,12V18H11V12H17Z"/>
                    </svg>
                    Current Rankings
                  </div>
                  
                  {displayData.map((entry, index) => (
                    <div key={index} className="leaderboard-item" style={{ animationDelay: `${index * 0.1}s` }}>
                      <div className="rank-number">{index + 1}</div>
                      <div className="user-avatar">
                        {entry.avatar || entry.username.charAt(0)}
                      </div>
                      <div className="user-info">
                        <div className="user-name">{entry.username}</div>
                        <div className="user-stats">
                          Fastest Time: {entry.fastestTime}s
                        </div>
                      </div>
                      <div className="score-badge">
                        {entry.totalScore}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;